import { useCallback, useMemo } from "react";
import type { ClientsWorkOverviewWorkRow } from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";
import {
    getWorkBoardColumns,
    getWorkRows,
} from "@components/organizationView/clients-work/board/workBoardColumns";
import { getWorkBoardColumnIcon } from "@components/organizationView/clients-work/board/workBoardStatus";
import { getWorkCardFieldColumns } from "@components/organizationView/clients-work/board/workCardFieldColumns";
import { Board } from "@design-system/Board/Board";
import { useConstant } from "@hooks/useConstant";
import type { BoardPlaygroundSettings } from "./boardPlaygroundSettings";
import { DEMO_WORK_STAGES, loadDemoWorkspaceRows } from "./fixtures/demoWork";
import { PlaygroundWorkCard } from "./PlaygroundWorkCard";

const NO_TEMPLATE_NAMES: ReadonlyMap<string, string> = new Map();
const NO_HIDDEN_FIELDS: ReadonlySet<string> = new Set();

const getRowId = (row: ClientsWorkOverviewWorkRow) => row.id;
const getRowLabel = (row: ClientsWorkOverviewWorkRow) => row.name;

/**
 * The playground's Work board: the demo clients' applications in stage
 * columns, as the real Work board draws them (Done and Canceled with their
 * icons, the other stages without), read only, with the Controls panel's
 * card fields, agent drawer and stress data set.
 */
export const WorkBoardPlayground = ({
    settings: { isStressMode, workCardFieldIds, agentDrawer },
}: {
    settings: BoardPlaygroundSettings;
}) => {
    const today = useConstant(() => new Date());
    const workspaceRows = useMemo(
        () => loadDemoWorkspaceRows({ today, isStressMode }),
        [today, isStressMode],
    );
    const rows = useMemo(() => getWorkRows(workspaceRows), [workspaceRows]);
    const { columns, getPlacements } = useMemo(
        () =>
            getWorkBoardColumns({
                columnBy: "stage",
                workspaceRows,
                stages: DEMO_WORK_STAGES,
                templateNames: NO_TEMPLATE_NAMES,
                isEmptyColumnsShown: true,
                getIcon: getWorkBoardColumnIcon,
            }),
        [workspaceRows],
    );
    const fieldColumns = useMemo(
        () =>
            getWorkCardFieldColumns({
                columns: workCardFieldIds.map((id) => ({ id })),
                cardFieldIds: undefined,
                hiddenFields: NO_HIDDEN_FIELDS,
            }),
        [workCardFieldIds],
    );
    const renderCard = useCallback(
        (row: ClientsWorkOverviewWorkRow) => (
            <PlaygroundWorkCard
                row={row}
                fieldColumns={fieldColumns}
                agentDrawer={agentDrawer}
            />
        ),
        [fieldColumns, agentDrawer],
    );
    return (
        <Board
            aria-label="Work"
            items={rows}
            columns={columns}
            getItemId={getRowId}
            getItemLabel={getRowLabel}
            getPlacements={getPlacements}
            renderCard={renderCard}
            emptyColumnLabel="No applications"
        />
    );
};
